/**
 * PublicLayout — wrapper des pages non-authentifiées (landing, login, etc.)
 * Refonte PASSAGE. Le reste de l'app (admin/responsable/etudiant/entreprise)
 * conserve son propre Layout et son skin Comptoir bleu/or — pas touché.
 */
import { Link, Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { APP_NAME, APP_PUBLISHER, type SessionUser } from '@lecomptoir/shared';
import { Sticker } from '@/components/passage/Sticker';

const HOME_BY_ROLE: Record<SessionUser['role'], string> = {
  super_admin: '/admin',
  responsable: '/responsable',
  student: '/etudiant',
  company: '/entreprise',
};

const HOME_LABEL_BY_ROLE: Record<SessionUser['role'], string> = {
  super_admin: 'Espace admin',
  responsable: 'Espace responsable',
  student: 'Mon espace',
  company: 'Mon espace',
};

export function PublicLayout() {
  // Lecture du cache TanStack Query SANS déclencher de fetch réseau.
  // Sur les pages publiques, on n'a pas besoin de vérifier la session :
  // si l'utilisateur est déjà connecté et a des données en cache (navigation
  // depuis l'app), on affiche son bouton "Mon espace" ; sinon on affiche la
  // nav anonyme. Évite le 401 systématique sur /auth/me pour les visiteurs.
  const qc = useQueryClient();
  const user = qc.getQueryData<SessionUser | null>(['auth', 'me']) ?? null;

  return (
    <div className="flex min-h-screen flex-col bg-white text-passage-ink">
      <a href="#main" className="skip-link">
        Aller au contenu principal
      </a>

      <header className="border-b-2 border-passage-ink bg-white">
        <nav className="container flex h-16 items-center justify-between" aria-label="Navigation principale">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="grid h-9 w-9 place-items-center rounded-[10px] border-2 border-passage-ink bg-passage-tomate text-[17px] font-black text-white shadow-passage-sm">
                C
              </span>
              <span className="text-[22px] font-black -tracking-[0.02em] text-passage-ink">
                {APP_NAME}<span className="text-passage-tomate">.</span>
              </span>
            </Link>
            <span className="hidden md:inline-block">
              <Sticker color="menthe" rotate={-3} shadow="xs">
                ★ Hôtellerie · Resto · Tourisme
              </Sticker>
            </span>
          </div>

          <ul className="flex items-center gap-4 text-sm font-semibold">
            {user ? (
              <>
                <li className="hidden text-passage-ink-soft sm:inline">{user.email}</li>
                <li>
                  <Link
                    to={HOME_BY_ROLE[user.role] ?? '/'}
                    className="rounded-full border-2 border-passage-ink bg-passage-ink px-4 py-2 font-extrabold text-white shadow-passage-sm transition hover:-translate-y-[1px] hover:shadow-passage"
                  >
                    {HOME_LABEL_BY_ROLE[user.role] ?? 'Mon espace'}
                  </Link>
                </li>
              </>
            ) : (
              <>
                <li className="hidden sm:inline">
                  <Link
                    to="/connexion"
                    className="inline-block rounded-full border-2 border-passage-ink bg-white px-4 py-2 font-extrabold text-passage-ink shadow-passage-sm transition hover:-translate-y-[1px] hover:shadow-passage"
                  >
                    Se connecter
                  </Link>
                </li>
                <li>
                  <Link
                    to="/inscription-entreprise"
                    className="inline-block rounded-full border-2 border-passage-ink bg-passage-ink px-4 py-2 font-extrabold text-white shadow-[3px_3px_0_#E63946] transition hover:translate-y-[2px] hover:shadow-[1px_1px_0_#E63946]"
                  >
                    Créer un compte →
                  </Link>
                </li>
              </>
            )}
          </ul>
        </nav>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t-2 border-passage-ink bg-passage-cream py-8 text-sm text-passage-ink-soft">
        <div className="container flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} {APP_PUBLISHER}. Tous droits réservés.</p>
          <ul className="flex flex-wrap gap-4 font-medium">
            <li><Link to="/mentions-legales" className="hover:text-passage-ink">Mentions légales</Link></li>
            <li><Link to="/confidentialite" className="hover:text-passage-ink">Confidentialité</Link></li>
            <li><Link to="/cgu" className="hover:text-passage-ink">CGU</Link></li>
            <li><a href="mailto:denis.bories@univ-tlse2.fr" className="hover:text-passage-ink">Contact</a></li>
          </ul>
        </div>
      </footer>
    </div>
  );
}
